/**
 * Print Letter chapter — Canva page-15 “A note for you”.
 * Contract 2×2, next-month bar, personal note, executive insight bar.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  Calendar,
  Check,
  ChevronsRight,
  Mail,
  X,
  Zap,
} from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import noteMountains from "../../../../assets/monthly-consultation/pdf/alignment-note-mountains.png";
import { PdfChapterShell, PdfCornerLuopan, PdfInsightStrip } from "../atoms";
import {
  clipPdfText,
  EXECUTIVE_REMINDER_QUOTE,
  letterCloseIntro,
  stripPrintDashes,
} from "../pdfCopy";
import {
  charcoal,
  green,
  paleGreen,
  palePink,
  paleTan,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF Letter chapter.
 */
export interface PdfChapterLetterProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/** Soft cap for the intro paragraph. */
const INTRO_CLIP = 220;
/** Soft cap for a contract cell. */
const CONTRACT_CLIP = 100;
/** Soft cap for the personal-note body. */
const NOTE_CLIP = 360;

/**
 * One tinted contract cell (DO THIS / BY WHEN / SUCCESS / DO NOT).
 */
interface ContractCellModel {
  /** Uppercase label. */
  readonly label: string;
  /** Live field copy. */
  readonly value: string;
  /** Icon tile and label color. */
  readonly accent: string;
  /** Card fill. */
  readonly tint: string;
  /** Lucide icon. */
  readonly Icon: LucideIcon;
}

/**
 * Thin gold rule with a diamond at the right end.
 *
 * @param props - Stroke color
 * @returns Rule row
 */
const PdfDiamondRule: React.FC<{ readonly color: string }> = ({ color }) => {
  const stroke =
    typeof color === "string" && color.trim().length > 0
      ? color.trim()
      : tanGold;
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <span
        style={{
          flex: 1,
          height: 1,
          background: stroke,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          marginLeft: 1,
        }}
      />
    </div>
  );
};

/**
 * Drop a leading profile-name prefix so the greeting line is not duplicated.
 *
 * @param text - Body or closing copy
 * @param name - Profile display name
 * @returns Copy without a leading `Name,` prefix
 */
function stripLeadingName(text: string, name: string): string {
  const t = typeof text === "string" ? text.trim() : "";
  const n = typeof name === "string" ? name.trim() : "";
  if (t.length === 0 || n.length === 0) {
    return t;
  }
  const prefix = `${n},`;
  if (t.toLowerCase().startsWith(prefix.toLowerCase())) {
    return t.slice(prefix.length).trim();
  }
  if (t.toLowerCase().startsWith(n.toLowerCase())) {
    return t.slice(n.length).replace(/^,/, "").trim();
  }
  return t;
}

/**
 * Strip chart-anchor tags so the Do This line stays clean.
 *
 * @param primaryMove - Contract primary move
 * @returns Cleaned action line
 */
function shortDoThis(primaryMove: string): string {
  const raw = typeof primaryMove === "string" ? primaryMove.trim() : "";
  if (raw.length === 0) {
    return "";
  }
  const withoutTag = raw.split(" · ")[0];
  const head =
    typeof withoutTag === "string" && withoutTag.trim().length > 0
      ? withoutTag.trim()
      : raw;
  return head.replace(/^Do this:\s*/i, "").trim();
}

/**
 * Build personal-note paragraphs from letter bodies, archetype copy, and closing.
 * Prefer real prose over empty card padding or large section gaps.
 *
 * @param bodies - Letter body paragraphs
 * @param identityLine - Archetype identity line
 * @param description - Archetype description for sparse letters
 * @param closing - Letter closing line
 * @param intro - Intro already shown above the contract grid
 * @param name - Profile name for stripping leading greetings
 */
function letterNoteParagraphs(
  bodies: string[],
  identityLine: string,
  description: string,
  closing: string,
  intro: string,
  name: string
): string[] {
  const introKey = intro.trim().toLowerCase();
  const parts: string[] = [];

  const pushUnique = (raw: string): void => {
    const trimmed = stripLeadingName(raw, name);
    if (trimmed.length === 0) {
      return;
    }
    const key = trimmed.toLowerCase();
    if (introKey.length > 0 && (key === introKey || introKey.includes(key))) {
      return;
    }
    if (parts.some((part) => part.toLowerCase() === key)) {
      return;
    }
    parts.push(trimmed);
  };

  for (const paragraph of bodies.slice(1)) {
    pushUnique(paragraph);
  }

  if (parts.length === 0) {
    pushUnique(description);
  }

  pushUnique(identityLine);
  pushUnique(closing);

  return parts;
}

/**
 * Resolve mountain art URL for the letter leftover band.
 *
 * @returns Image URL, or empty string when unavailable
 */
function letterArtSrc(): string {
  return typeof noteMountains === "string" ? noteMountains.trim() : "";
}

/**
 * Closing note with contract 2×2, next-month bar, personal note, and insight.
 */
export const PdfChapterLetter: React.FC<PdfChapterLetterProps> = ({
  bundle,
  pageNumber,
}) => {
  const letter = bundle.letter;
  const profileName =
    typeof bundle.profileName === "string" ? bundle.profileName.trim() : "";
  const greeting =
    typeof letter.greeting === "string" ? letter.greeting.trim() : "";
  const closing =
    typeof letter.closing === "string" ? letter.closing.trim() : "";
  const bodies = Array.isArray(letter.bodyParagraphs)
    ? letter.bodyParagraphs.filter(
        (p): p is string => typeof p === "string" && p.trim().length > 0
      )
    : [];
  const firstBody = bodies.length > 0 ? bodies[0] : "";
  const greetingName =
    greeting.length > 0
      ? greeting.replace(/,$/, "").trim()
      : profileName;
  const introProse = clipPdfText(
    stripLeadingName(
      typeof firstBody === "string" && firstBody.trim().length > 0
        ? firstBody
        : closing,
      greetingName
    ),
    INTRO_CLIP
  );
  const introFallback = letterCloseIntro(greetingName, closing, "");
  const intro = introProse.length > 0 ? introProse : clipPdfText(introFallback, INTRO_CLIP);

  const close = letter.operationalClose;
  const nextBridge =
    typeof close.nextMonthBridge === "string"
      ? close.nextMonthBridge.trim()
      : "";

  const identityLine =
    typeof bundle.archetype.identityLine === "string"
      ? bundle.archetype.identityLine.trim()
      : "";
  const archetypeDescription =
    typeof bundle.archetype.description === "string"
      ? bundle.archetype.description.trim()
      : "";
  const noteParagraphs = letterNoteParagraphs(
    bodies,
    identityLine,
    archetypeDescription,
    closing,
    intro,
    greetingName
  ).map((paragraph) => clipPdfText(paragraph, NOTE_CLIP));
  const noteJoined = noteParagraphs.join(" ");
  const noteTitle =
    profileName.length > 0
      ? `Personal note for ${profileName}`
      : "Personal note";
  const noteSignParts: string[] = [];
  if (profileName.length > 0) {
    noteSignParts.push(profileName);
  }
  noteSignParts.push(`M${String(bundle.lunarMonth)} ${String(bundle.solarYear)}`);
  const noteSign = noteSignParts.join(" · ");
  const artSrc = letterArtSrc();

  const doThis = clipPdfText(
    shortDoThis(
      typeof close.decision === "string"
        ? close.decision
        : bundle.monthContract.primaryMove
    ),
    CONTRACT_CLIP
  );
  const byWhen = clipPdfText(
    typeof close.deadline === "string" && close.deadline.trim().length > 0
      ? close.deadline
      : bundle.monthContract.deadline,
    CONTRACT_CLIP
  );
  const success = clipPdfText(
    typeof close.successMeasure === "string" &&
      close.successMeasure.trim().length > 0
      ? close.successMeasure
      : bundle.monthContract.successMeasure,
    CONTRACT_CLIP
  );
  const doNot = clipPdfText(
    typeof bundle.monthContract.antiPattern === "string"
      ? bundle.monthContract.antiPattern
      : "",
    CONTRACT_CLIP
  );

  const cells: ContractCellModel[] = [
    {
      label: "Do this",
      value: doThis.length > 0 ? doThis : "Complete this month's primary move.",
      accent: terracotta,
      tint: palePink,
      Icon: Zap,
    },
    {
      label: "By when",
      value: byWhen.length > 0 ? byWhen : "Before month end",
      accent: tanGold,
      tint: paleTan,
      Icon: Calendar,
    },
    {
      label: "Success",
      value:
        success.length > 0
          ? success
          : "You can name what changed in one clear sentence.",
      accent: green,
      tint: paleGreen,
      Icon: Check,
    },
    {
      label: "Do not",
      value: doNot.length > 0 ? doNot : "Do not repeat this month's named trap.",
      accent: terracotta,
      tint: palePink,
      Icon: X,
    },
  ];

  const insight =
    identityLine.length > 0 && !noteJoined.includes(identityLine)
      ? identityLine
      : closing.length > 0 && intro.indexOf(closing) < 0
        ? closing
        : EXECUTIVE_REMINDER_QUOTE;

  return (
    <PdfChapterShell
      chapterId="letter"
      eyebrow=""
      title=""
      hideHeader={true}
      breakBefore="always"
      fillPage={true}
      compact
      pageNumber={pageNumber}
    >
      <div className="mc-pdf-letter-page">
        <div className="mc-pdf-corner-luopan-band mc-pdf-letter-hero">
          <PdfCornerLuopan />
          <div className="mc-pdf-letter-head">
            <span aria-hidden="true" className="mc-pdf-letter-head-icon">
              <Mail size={20} color={terracotta} strokeWidth={1.8} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p className="mc-pdf-letter-eyebrow">+ CLOSE</p>
              <h2 className="mc-pdf-letter-title">A note for you</h2>
            </div>
          </div>
          <div style={{ position: "relative", zIndex: 1, marginBottom: 12 }}>
            <PdfDiamondRule color={tanGold} />
          </div>
          {greetingName.length > 0 ? (
            <p className="mc-pdf-letter-greeting">{`${greetingName},`}</p>
          ) : null}
          {intro.length > 0 ? (
            <p className="mc-pdf-letter-intro">{stripPrintDashes(intro)}</p>
          ) : null}
        </div>

        <div className="mc-pdf-letter-stack">
          <div className="mc-pdf-letter-contract-grid">
            {cells.map((cell) => {
              const Icon = cell.Icon;
              return (
              <div
                key={cell.label}
                className="mc-pdf-card mc-pdf-letter-contract-cell"
                style={{
                  background: cell.tint,
                  border: `1px solid ${cell.accent}33`,
                }}
              >
                <span
                  aria-hidden="true"
                  className="mc-pdf-letter-contract-icon"
                  style={{ background: `${cell.accent}22` }}
                >
                  <Icon size={18} color={cell.accent} strokeWidth={2} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <p
                    className="mc-pdf-letter-contract-label"
                    style={{ color: cell.accent }}
                  >
                    {cell.label}
                  </p>
                  <p className="mc-pdf-letter-contract-value">
                    {stripPrintDashes(cell.value)}
                  </p>
                </div>
              </div>
              );
            })}
          </div>

          {nextBridge.length > 0 ? (
            <div className="mc-pdf-card mc-pdf-letter-next">
              <ChevronsRight size={18} color={charcoal} strokeWidth={2} />
              <p className="mc-pdf-letter-next-text">
                {nextBridge.startsWith("Next:")
                  ? stripPrintDashes(nextBridge)
                  : `Next: ${stripPrintDashes(nextBridge)}`}
              </p>
            </div>
          ) : null}

          {noteParagraphs.length > 0 ? (
            <div className="mc-pdf-card mc-pdf-letter-note">
              <span aria-hidden="true" className="mc-pdf-letter-note-quote">
                “
              </span>
              <p className="mc-pdf-letter-note-title">{noteTitle}</p>
              {noteParagraphs.map((paragraph, index) => (
                <p
                  key={`note-${String(index)}`}
                  className="mc-pdf-letter-note-body"
                  style={index > 0 ? { marginTop: 8 } : undefined}
                >
                  {stripPrintDashes(paragraph)}
                </p>
              ))}
              <div className="mc-pdf-letter-note-footer">
                <p className="mc-pdf-letter-note-complete">Consultation Brief Complete</p>
                {noteSign.length > 0 ? (
                  <p className="mc-pdf-letter-note-sign">{noteSign}</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {artSrc.length > 0 ? (
          <div className="mc-pdf-letter-art-band" aria-hidden="true">
            <img src={artSrc} alt="" className="mc-pdf-letter-art" />
          </div>
        ) : (
          <div className="mc-pdf-letter-art-band" aria-hidden="true" />
        )}

        <PdfInsightStrip
          label="Executive insight"
          text={clipPdfText(stripPrintDashes(insight), 160)}
          variant="executiveBar"
        />
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterLetter;
